// ============================================================
// TMR Portal Admin — the operator's permissions, available to any screen
// ============================================================
import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AuthorizationScope, EnvironmentCapabilities } from '@tmr/auth';

/**
 * Permission keys the portal gates on. They are resolved by the backend
 * (`GET /me/authorization`) and are the only source of authorization in the UI — never a Clerk role
 * (skill `frontend-architecture`, Regra 5).
 */
export const PERMISSIONS = {
  companyRead: 'company.read',
  companyOnboard: 'company.onboard',
  companyUpdate: 'company.update',
  /**
   * Bringing the legacy's whole producer base across at once (19 §3). Not `companyOnboard`, which also gates
   * importing one chosen producer: creating a thousand Empresas is a different blast radius from filing
   * one. Gates the /empresas/importar screen.
   */
  companyImportAdmin: 'company.import.admin',
  /**
   * Reading the applications clinics send from the public site (`28` §9) — gates Empresas › Solicitações.
   * Not `companyRead`: an application carries personal data of people who are not clients yet.
   */
  companyApplicationsRead: 'company.applications.read',
  /** Taking and rejecting an application. Approving one also needs `companyOnboard` — it creates the Empresa. */
  companyApplicationsManage: 'company.applications.manage',
  usersManage: 'users.manage',
  /**
   * Creating an operator's staff login in the legacy back-office (the Portal Interno) from the Users screen
   * (33). Not `usersManage`: the login opens a system whose screens move money.
   */
  usersLegacyAccessGrant: 'users.legacy_access.grant',
  /** Finding somebody in the Parties directory. Split from `parties.manage` in B8.4 — registering an
   *  Empresa needs to *find* the representative, not to be able to rewrite the whole directory. */
  partiesRead: 'parties.read',
  partiesManage: 'parties.manage',
  /** Reading contracts (M3). Gates the Contratos screen and the Empresa's contracts tab. */
  contractsRead: 'contracts.read',
  /** Drawing up a contract and sending it for signature — the two writes the Contracts module exposes. */
  contractsManage: 'contracts.manage',
  /**
   * Reading the contracts patients signed (`24`). Gates /contratos-paciente, and also opens the
   * "Contrato de Paciente" tab of an Empresa's timeline.
   */
  patientContractsRead: 'patient.contracts.read',
  /**
   * Acting on an issued patient contract — today only asking the v1.1 routine to retry one whose PDF or
   * signed copy failed. It does not draw contracts up: issuing is the other system's, driven by a sale.
   */
  patientContractsManage: 'patient.contracts.manage',
  /**
   * Reading the patient contract templates and their versions (`24`). Gates /modelos-contrato.
   *
   * Separate from `contractsRead`, which is the Empresa's own accreditation contract: sharing a key would
   * hand each one to whoever may only see the other.
   */
  contractTemplatesRead: 'contract.templates.read',
  /**
   * Writing a patient contract template: opening a draft, editing, publishing a version, and creating or
   * archiving a clinic's override.
   *
   * **The widest key in that module.** Publishing the global template changes the text every patient of
   * every clinic signs from that moment on — the same reason `rates.global.manage` is not bundled with
   * `company.rates.manage`.
   */
  contractTemplatesManage: 'contract.templates.manage',
  /** Reading an Empresa's rates — gates the Taxas tab (08 §7). */
  companyRatesRead: 'company.rates.read',
  /**
   * Changing what the TMR charges an Empresa: the rate CRUD, the temporary rate, and reconciling with the
   * legacy. Not `company.update` — correcting an address and repricing are different responsibilities.
   */
  companyRatesManage: 'company.rates.manage',
  /**
   * Rate operations across the whole portfolio (15 R3): reconciling every Empresa against the legacy, and
   * reading which ones are out of step with it. Gates the /taxas/sincronizacao screen.
   *
   * Separate from `companyRatesManage` because the blast radius is — that one changes what one clinic pays,
   * this one rewrites the V2's copy of what every clinic pays. The operator's Empresa scope still applies.
   */
  companyRatesAdmin: 'company.rates.admin',
  /** Reading the TMR's global rate template (15 §3 A) — gates the /taxas screen. */
  ratesGlobalRead: 'rates.global.read',
  /**
   * Changing the global template, and propagating a line onto the Empresas that already hold it.
   *
   * Deliberately not `company.rates.manage`: that one lets an operator negotiate one clinic's rate, this
   * one changes what the TMR charges everyone from here on and can rewrite every existing Empresa in a
   * single call. Sharing a key would make granting the first quietly grant the second (15 §7).
   */
  ratesGlobalManage: 'rates.global.manage',
  /** Reading the TMR's default for every business rule (`25`) — gates the /regras-negocio screen. */
  businessRulesGlobalRead: 'business_rules.global.read',
  /**
   * Changing the TMR's default for a business rule — how every clinic without its own value sells from the
   * next sale on. Deliberately not `companyBusinessRulesManage`, for the reason `ratesGlobalManage` is not
   * `companyRatesManage`: one clinic's exception and everybody's rule are different responsibilities.
   */
  businessRulesGlobalManage: 'business_rules.global.manage',
  /**
   * Reading an Empresa's business rules (`25`) — gates the "Regras de negócio" tab, and on the timeline the
   * category of the same name.
   */
  companyBusinessRulesRead: 'company.business_rules.read',
  /** Giving an Empresa its own value for a business rule, and taking it away (`25`). */
  companyBusinessRulesManage: 'company.business_rules.manage',
  /**
   * Reading the credit rating's queue, an evaluation and a customer's credit (`26`) — gates the Crédito group, and
   * on the timeline the category of the same name.
   */
  creditReviewsRead: 'credit.reviews.read',
  /** Approving or rejecting a sale the credit rating held back (`26` §3.2). */
  creditReviewsManage: 'credit.reviews.manage',
  /** Requesting a new, paid report from the bureau (`26` §3.3) — separate so the queue does not grant spending. */
  creditBureauQuery: 'credit.bureau.query',
  /**
   * Entering the Contingência panel (`30`) — gates the menu entry, the kinds and the request list, and on the
   * timeline the category of the same name. A door, not a power: using a workaround takes its own key.
   */
  contingencyAccess: 'contingency.access',
  /**
   * The Contingência manager (`30` §8): switching each workaround on or off, requiring approval for it, and
   * approving or rejecting the requests that wait — never one they opened themselves.
   */
  contingencyManage: 'contingency.manage',
  /**
   * Asking for a wildcard code (código coringa) and generating it once approved. One key per kind of contingency,
   * so granting one workaround never grants another.
   */
  contingencyWildcardCodeCreate: 'contingency.wildcard_code.create',
  /** Reading an Empresa's receiving accounts (13) — gates the Conta tab. */
  financeRead: 'finance.read',
  /** Adding, correcting and choosing an Empresa's receiving account (13). */
  financeManage: 'finance.manage',
  authorizationAdmin: 'authorization.admin',
} as const;

/** A permission key the portal knows about. */
export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Everything `GET /me/authorization` said, in the shape the screens ask it in. */
interface AuthorizationContextValue {
  granted: ReadonlySet<string>;
  scope: AuthorizationScope | null;
  capabilities: EnvironmentCapabilities;
}

const PermissionsContext = createContext<AuthorizationContextValue | null>(null);

/** Props for {@link PermissionsProvider}. */
interface PermissionsProviderProps {
  /** The permissions resolved by the backend for this session. */
  permissions: readonly string[];
  /** How far the operator's own reach goes (10 B6b) — null on the legacy Organizations path. */
  scope: AuthorizationScope | null;
  /** What this deployment is wired for, regardless of who is asking. */
  capabilities: EnvironmentCapabilities;
  children: ReactNode;
}

/**
 * Publishes the session's authorization to the screen tree — permissions, scope and capabilities.
 *
 * It exists because `useTmrAuthz` keeps its state per call rather than in a context: a screen calling it
 * would run a second Clerk session exchange and a second `/me/authorization` fetch. All three come from
 * that one payload, are read once in `App.tsx`, and are handed down from here.
 *
 * They answer three different questions, which is why they are not collapsed into one: permissions are
 * *may I*, scope is *over what*, capabilities are *does this environment even have it*.
 */
export function PermissionsProvider({ permissions, scope, capabilities, children }: PermissionsProviderProps) {
  const value = useMemo<AuthorizationContextValue>(
    () => ({ granted: new Set(permissions), scope, capabilities }),
    [permissions, scope, capabilities],
  );
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/**
 * Whether the operator holds a permission.
 *
 * Hiding a control is a courtesy, not a defence: the server refuses the request either way, and a `403`
 * still has to be shown rather than swallowed (skill `frontend-architecture`, Regra 5).
 *
 * @param permission The key to check.
 * @returns True when the session carries it.
 */
export function usePermission(permission: PermissionKey): boolean {
  return useAuthorizationContext('usePermission').granted.has(permission);
}

/**
 * How far the operator's own reach goes (10 B6b) — null on the legacy Organizations path.
 *
 * A screen needs this when it offers to grant something: an operator who is not unscoped cannot grant
 * unscoped access, so offering that option would be offering a guaranteed 403 (`RoleGrantValidator`,
 * 06 §5.1).
 */
export function useOperatorScope(): AuthorizationScope | null {
  return useAuthorizationContext('useOperatorScope').scope;
}

/**
 * What this deployment is wired for, regardless of who is asking.
 *
 * Distinct from a permission: a capability being false is not the operator lacking something, it is the
 * environment not having it. A screen uses it to say what is true rather than to infer it from a status
 * code that several other faults produce.
 */
export function useCapabilities(): EnvironmentCapabilities {
  return useAuthorizationContext('useCapabilities').capabilities;
}

/** The context, or a failure naming the hook that was called outside the provider. */
function useAuthorizationContext(hook: string): AuthorizationContextValue {
  const value = useContext(PermissionsContext);
  if (!value) throw new Error(`${hook} precisa estar dentro de <PermissionsProvider>.`);
  return value;
}

/**
 * A predicate over the session's permissions — for filtering a list (e.g. the navigation), where calling
 * {@link usePermission} once per item would break the rules of hooks.
 *
 * @returns A function that answers whether the session holds a given permission.
 */
export function useHasPermission(): (permission: PermissionKey) => boolean {
  const { granted } = useAuthorizationContext('useHasPermission');
  return (permission) => granted.has(permission);
}
